import { createBrowserRouter, Navigate, Outlet, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, ArrowRight, CalendarDays, CheckSquare, Clock, RefreshCw, Users } from 'lucide-react';
import App from '../App';
import { useAuth } from '../contexts/AuthContext';
import Login from '../components/auth/Login';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import RoleBasedRoute from '../components/auth/RoleBasedRoute';
import AdminDashboard from '../components/admin/Dashboard';
import UserList from '../components/admin/UserList';
import CreateUser from '../components/admin/CreateUser';
import EditUser from '../components/admin/EditUser';
import SkillManagement from '../components/admin/SkillManagement';
import RoleManagement from '../components/admin/RoleManagement';
import RoleShiftDefaults from '../components/admin/RoleShiftDefaults';
import PayrollView from '../components/admin/PayrollView';
import TimesheetSubmission from '../components/timesheets/TimesheetSubmission';
import MyTimesheets from '../components/timesheets/MyTimesheets';
import TimesheetApproval from '../components/timesheets/TimesheetApproval';
import ShiftPatternManager from '../components/admin/ShiftPatternManager';
import Layout from '../components/layout/Layout';
import LeaveRequestForm from '../components/leave/LeaveRequestForm';
import SwapMarketplace from '../components/swaps/SwapMarketplace';
import ApprovalDashboard from '../components/admin/AdminDashboard';
import RosterDashboard from '../components/roster/RosterDashboard';
import RosterPlayground from '../components/roster/RosterPlayground';
import MySchedule from '../components/roster/MySchedule';
import MyRoster from '../components/roster/MyRoster';
import TeamRoster from '../components/roster/TeamRoster';
import RosterAvailabilityNotice from '../components/roster/RosterAvailabilityNotice';
import Settings from '../components/settings/Settings';
import { UserRole } from '../types/auth';
import apiClient from '../services/api';
import { checkCoverage, CoverageReport, getPublishedRoster, getRosterPublicationStatus, RosterPublicationStatus, getShifts, Shift } from '../services/rosterService';
import { getStaffShiftLabel } from '../utils/rosterStyles';

// Root redirect: unauthenticated → /login, authenticated → /dashboard
const RootRedirect = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};

const getRoleName = (role: unknown) => {
  if (typeof role === 'string') return role;
  if (role && typeof role === 'object' && 'name' in role) return String((role as any).name);
  return '';
};

const hasAnyRole = (role: string, allowedRoles: string[]) => {
  const normalizedRole = role.toUpperCase();
  return allowedRoles.some(allowed => normalizedRole === allowed.toUpperCase());
};

const ControllerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [coverage, setCoverage] = useState<CoverageReport | null>(null);
  const [workingStaff, setWorkingStaff] = useState<any[]>([]);
  const [pendingTimesheets, setPendingTimesheets] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [pendingSwaps, setPendingSwaps] = useState(0);
  const [publicationStatus, setPublicationStatus] = useState<RosterPublicationStatus | null>(null);

  const loadDashboard = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);
    const today = new Date();
    const todayKey = format(today, 'yyyy-MM-dd');

    try {
      const [roster, rosterStatus, coverageReport, timesheets, leaves, swaps] = await Promise.all([
        getPublishedRoster(today.getFullYear(), today.getMonth() + 1).catch(() => null),
        getRosterPublicationStatus(today.getFullYear(), today.getMonth() + 1).catch(() => null),
        checkCoverage(today).catch(() => null),
        apiClient.get('/timesheets', { params: { status: 'PENDING' } }).catch(() => ({ data: { data: [] } })),
        apiClient.get('/leaves').catch(() => ({ data: [] })),
        apiClient.get('/swaps?type=all').catch(() => ({ data: [] })),
      ]);

      setPublicationStatus(rosterStatus);
      const staffOnDuty = (roster?.staff || [])
        .map((member: any) => {
          const shift = member.shifts?.[todayKey];
          return shift ? { member, shift } : null;
        })
        .filter(Boolean);

      setWorkingStaff(staffOnDuty);
      setCoverage(coverageReport);
      setPendingTimesheets((timesheets.data.data || []).length);
      setPendingLeaves((Array.isArray(leaves.data) ? leaves.data : leaves.data?.data || []).filter((item: any) => item.status === 'PENDING').length);
      setPendingSwaps((Array.isArray(swaps.data) ? swaps.data : swaps.data?.data || []).filter((item: any) => item.status === 'PENDING').length);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const totalRequests = pendingLeaves + pendingSwaps;
  const alerts = coverage?.alerts || [];
  const coverageEntries = Object.entries(coverage?.coverage || {});

  if (loading) {
    return (
      <div className="space-y-4 max-w-6xl">
        <div className="h-8 bg-slate-200 rounded-lg w-64 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(item => <div key={item} className="h-32 bg-white border border-slate-200 rounded-lg animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Controller Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Today’s roster, coverage, and approvals</p>
        </div>
        <button
          onClick={() => loadDashboard(true)}
          disabled={refreshing}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {publicationStatus && publicationStatus !== 'PUBLISHED' && (
        <RosterAvailabilityNotice status={publicationStatus} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">On duty today</p>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <p className="mt-2 text-3xl font-bold text-slate-900">{workingStaff.length}</p>
          <p className="text-sm text-slate-500">{format(new Date(), 'EEE, d MMM')}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Coverage alerts</p>
            <AlertTriangle className={`w-5 h-5 ${alerts.length ? 'text-red-500' : 'text-emerald-500'}`} />
          </div>
          <p className="mt-2 text-3xl font-bold text-slate-900">{alerts.length}</p>
          <p className="text-sm text-slate-500">{alerts.length ? 'Needs attention' : 'No alerts'}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Timesheets</p>
            <CheckSquare className="w-5 h-5 text-amber-500" />
          </div>
          <p className="mt-2 text-3xl font-bold text-slate-900">{pendingTimesheets}</p>
          <p className="text-sm text-slate-500">Pending review</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Requests</p>
            <Clock className="w-5 h-5 text-purple-500" />
          </div>
          <p className="mt-2 text-3xl font-bold text-slate-900">{totalRequests}</p>
          <p className="text-sm text-slate-500">{pendingLeaves} leave · {pendingSwaps} swaps</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Today’s Team</h2>
              <p className="text-sm text-slate-500">Confirmed rostered staff</p>
            </div>
            <Link to="/controller/roster" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600">
              Full roster <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {publicationStatus && publicationStatus !== 'PUBLISHED' ? (
            <div className="py-10 text-center text-slate-500">Confirmed roster unavailable until publication.</div>
          ) : workingStaff.length === 0 ? (
            <div className="py-10 text-center text-slate-500">No staff are rostered today.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {workingStaff.slice(0, 8).map(({ member, shift }) => (
                <div key={`${member.id}-${shift.id}`} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{member.name}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{member.role}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-slate-800">{getStaffShiftLabel(shift)}</p>
                    <p className="text-xs text-slate-500">{shift.location || 'Rostered shift'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h2 className="text-lg font-bold text-slate-800">Coverage</h2>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {coverageEntries.length === 0 ? (
                <div className="col-span-2 text-sm text-slate-500">Coverage data unavailable.</div>
              ) : coverageEntries.map(([type, count]) => (
                <div key={type} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-bold uppercase text-slate-500">{type}</p>
                  <p className="text-xl font-bold text-slate-900">{count}</p>
                </div>
              ))}
            </div>
            {alerts.length > 0 && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-xs font-bold uppercase text-red-600 mb-1">Alerts</p>
                {alerts.slice(0, 3).map((alert, index) => <p key={index} className="text-sm text-red-700">{alert}</p>)}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
            <Link to="/timesheets/approvals" className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between">
              <span className="font-semibold text-slate-800">Review timesheets</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link to="/payroll" className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between">
              <span className="font-semibold text-slate-800">Payroll export</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link to="/team-roster" className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between">
              <span className="font-semibold text-slate-800">Phone roster view</span>
              <CalendarDays className="w-4 h-4 text-slate-400" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
const UserDashboard = () => {
  const { user } = useAuth();
  const [todayShift, setTodayShift] = useState<Shift | null>(null);
  const [nextShift, setNextShift] = useState<Shift | null>(null);
  const [todayTimesheet, setTodayTimesheet] = useState<any>(null);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [publicationStatus, setPublicationStatus] = useState<RosterPublicationStatus | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;
      const now = new Date();
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(now);
      todayEnd.setHours(23, 59, 59, 999);
      const futureEnd = new Date(now);
      futureEnd.setDate(futureEnd.getDate() + 30);

      const [today, upcoming, rosterStatus, timesheets, leaves, swaps] = await Promise.all([
        getShifts(todayStart, todayEnd, user.id),
        getShifts(now, futureEnd, user.id),
        getRosterPublicationStatus(now.getFullYear(), now.getMonth() + 1).catch(() => null),
        apiClient.get('/timesheets/my'),
        apiClient.get('/leaves').catch(() => ({ data: [] })),
        apiClient.get('/swaps').catch(() => ({ data: [] })),
      ]);

      setPublicationStatus(rosterStatus);
      setTodayShift(today[0] || null);
      setNextShift(upcoming.find((shift: Shift) => !today[0] || shift.id !== today[0].id) || null);
      const todayKey = todayStart.toISOString().split('T')[0];
      setTodayTimesheet((timesheets.data.data || []).find((ts: any) => ts.shiftDate?.startsWith(todayKey) || ts.shiftId === today[0]?.id) || null);
      const leaveCount = (Array.isArray(leaves.data) ? leaves.data : []).filter((item: any) => item.status === 'PENDING').length;
      const swapCount = (Array.isArray(swaps.data) ? swaps.data : []).filter((item: any) => item.status === 'PENDING').length;
      setPendingRequests(leaveCount + swapCount);
    };
    load().catch(() => {});
  }, [user?.id]);

  const formatShift = (shift: Shift | null) => {
    if (!shift) return 'No rostered shift';
    return 'Rostered shift';
  };

  const rosterUnavailable = Boolean(publicationStatus && publicationStatus !== 'PUBLISHED');
  const canSubmitToday = Boolean(publicationStatus === 'PUBLISHED' && todayShift && !todayTimesheet);

  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">My Dashboard</h1>
        <p className="text-slate-500 text-sm mt-0.5">Today’s operations</p>
      </div>
      {publicationStatus && publicationStatus !== 'PUBLISHED' && (
        <RosterAvailabilityNotice status={publicationStatus} />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase">Today’s shift</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{rosterUnavailable ? (publicationStatus === 'UNPUBLISHED' ? 'Roster temporarily unavailable' : 'Roster not published yet') : formatShift(todayShift)}</p>
          {!rosterUnavailable && todayShift && <p className="text-sm text-slate-500">{getStaffShiftLabel(todayShift)}</p>}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase">Next shift</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{formatShift(nextShift)}</p>
          {nextShift && <p className="text-sm text-slate-500">{new Date(nextShift.startTime).toLocaleDateString()} · {getStaffShiftLabel(nextShift)}</p>}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase">Today’s timesheet</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{rosterUnavailable ? (publicationStatus === 'UNPUBLISHED' ? 'Unavailable during amendment' : 'Unavailable until publication') : todayTimesheet?.status || (todayShift ? 'Not submitted' : 'Unavailable')}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase">Pending requests</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{pendingRequests}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to={canSubmitToday ? `/timesheets/submit?shiftId=${todayShift?.id}` : '/timesheets/schedule'} className={`rounded-lg px-4 py-3 font-semibold text-center ${canSubmitToday ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
          {rosterUnavailable ? 'Hours unavailable' : 'Submit hours'}
        </Link>
        <Link to="/my-roster" className="rounded-lg px-4 py-3 font-semibold text-center bg-white border border-slate-200 text-slate-700">My roster</Link>
        <Link to="/team-roster" className="rounded-lg px-4 py-3 font-semibold text-center bg-white border border-slate-200 text-slate-700">Team roster</Link>
        <Link to="/swaps" className="rounded-lg px-4 py-3 font-semibold text-center bg-white border border-slate-200 text-slate-700">Swaps/leave</Link>
      </div>
    </div>
  );
};
const Unauthorized = () => (
  <div className="p-8 max-w-md">
    <div className="bg-red-50 border border-red-200 rounded-xl p-6">
      <h1 className="text-xl font-bold text-red-700">Access Denied</h1>
      <p className="text-red-600 text-sm mt-1">You do not have permission to view this page.</p>
    </div>
  </div>
);

const DashboardRoute = () => {
  const { user } = useAuth();
  const role = getRoleName(user?.role);

  if (hasAnyRole(role, ['Super Admin', 'ADMIN', 'Administrator', 'System Administrator', 'Station Manager'])) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (hasAnyRole(role, ['CONTROLLER', 'Controller'])) {
    return <Navigate to="/controller/dashboard" replace />;
  }

  return <UserDashboard />;
};

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'unauthorized',
        element: <Unauthorized />,
      },
      {
        element: (
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'dashboard',
            element: <DashboardRoute />,
          },
          {
            path: 'my-roster',
            element: <MyRoster />,
          },
          {
            path: 'team-roster',
            element: <TeamRoster />,
          },
          {
            path: 'settings',
            element: <Settings />,
          },
          {
            path: 'payroll',
            element: (
              <RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.CONTROLLER, 'OFFICE', 'Office']}>
                <PayrollView />
              </RoleBasedRoute>
            ),
          },
          {
            path: 'admin',
            element: (
              <RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
                <Outlet />
              </RoleBasedRoute>
            ),
            children: [
              {
                path: 'dashboard',
                element: <AdminDashboard />,
              },
              {
                path: 'users',
                element: <UserList />,
              },
              {
                path: 'users/create',
                element: <CreateUser />,
              },
              {
                path: 'users/edit/:id',
                element: <EditUser />,
              },
              {
                path: 'skills',
                element: <SkillManagement />,
              },
              {
                path: 'roles',
                element: <RoleManagement />,
              },
              {
                path: 'payroll',
                element: <Navigate to="/payroll" replace />,
              },
              {
                path: 'approvals',
                element: <ApprovalDashboard />,
              },
              {
                path: 'roster',
                element: <RosterDashboard />,
              },
              {
                path: 'roster-playground',
                element: <RosterPlayground />,
              },
              {
                path: 'patterns',
                element: <ShiftPatternManager />,
              },
              {
                path: 'role-shift-defaults',
                element: <RoleShiftDefaults />,
              },

            ],
          },
          {
            path: 'controller',
            element: (
              <RoleBasedRoute allowedRoles={[UserRole.CONTROLLER]}>
                <Outlet />
              </RoleBasedRoute>
            ),
            children: [
              {
                path: 'dashboard',
                element: <ControllerDashboard />,
              },
              {
                path: 'roster',
                element: <TeamRoster />,
              },
            ],
          },
          {
            path: 'leave',
            children: [
              {
                path: 'request',
                element: <LeaveRequestForm />,
              },
            ],
          },
          {
            path: 'swaps',
            element: <SwapMarketplace />,
          },
          {
            path: 'timesheets',
            children: [
              {
                path: 'submit',
                element: <TimesheetSubmission />,
              },
              {
                path: 'my',
                element: <MyTimesheets />,
              },
              {
                path: 'schedule',
                element: <MySchedule />,
              },
              {
                path: 'approvals',
                element: (
                  <RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN, 'CONTROLLER', 'ADMIN', 'OFFICE', 'Office']}>
                    <TimesheetApproval />
                  </RoleBasedRoute>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
]);
